import React, { useEffect, useState, useCallback, useRef } from 'react';
import { WorkspaceConfig } from '../../types/onboarding';
import { Image } from '../../../ui/Image';
import api from '../../../../lib/api';
import { Button } from '../../components/Button';
import { useQueryClient } from '@tanstack/react-query';
import { useDisconnectSlack, useWorkspaces, WorkspaceType } from '../../apis/workspace-registry';

import { useTranslation } from 'react-i18next';

interface SlackSetupScreenProps {
  workspace: WorkspaceConfig;
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

interface SlackInstallButtonProps {
  onSuccess?: () => Promise<void>;
  onError?: (error: string) => void;
  disabled?: boolean;
  isReconfiguring?: boolean;
}

const SlackInstallButton: React.FC<SlackInstallButtonProps> = ({
  onSuccess,
  onError,
  disabled = false,
  isReconfiguring = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const windowRef = useRef<Window | null>(null);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (windowRef.current) {
      windowRef.current = null;
    }
    // Remove message event listener
    window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle incoming messages from the OAuth window
  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      // TODO: Verify the origin matches our app URL
      // Verify the origin matches our app URL
      // if (event.origin !== window.location.origin) {
      //   return;
      // }

      const result = event.data;
      if (
        !['SLACK_OAUTH_SUCCESS', 'SLACK_OAUTH_ERROR'].includes(result?.type)
      ) {
        return;
      }

      if (result && Date.now() - result.timestamp < 5 * 60 * 1000) {
        if (result.type === 'SLACK_OAUTH_SUCCESS') {
          await onSuccess?.();
        } else if (result.type === 'SLACK_OAUTH_ERROR') {
          onError?.(result.message || t('installationFailed'));
        }
      }
      cleanup();
      setIsLoading(false);
    },
    [onSuccess, onError, cleanup, t],
  );

  // Cleanup on unmount
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => cleanup();
  }, [handleMessage, cleanup]);

  // Check if window is closed periodically
  const checkWindow = useCallback(() => {
    if (windowRef.current?.closed) {
      cleanup();
      setIsLoading(false);
    }
  }, [cleanup]);

  const handleInstall = async () => {
    try {
      setIsLoading(true);

      // Get the OAuth URL using our API client
      const { data } = await api.get(
        '/api/core/pleri/workspaces/slack/oauth/init',
      );

      if (!data?.url) {
        throw new Error('Failed to get Slack installation URL');
      }

      // Open Slack OAuth in new window
      windowRef.current = window.open(data.url, '_blank');

      // Set up interval to check if window is closed
      intervalRef.current = setInterval(checkWindow, 1000);
    } catch (error) {
      cleanup();
      setIsLoading(false);
      onError?.(error instanceof Error ? error.message : 'Installation failed');
    }
  };

  return (
    <Button
      onClick={handleInstall}
      disabled={isLoading || disabled}
      variant="primary"
      className="group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all"
    >
      {isLoading ? (
        <div className="flex items-center justify-center space-x-2">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          <span>{t('initializing')}</span>
        </div>
      ) : (
        <>
          <span>
            {isReconfiguring ? t('reconfigureSlack') : t('addToSlack')}
          </span>
        </>
      )}
    </Button>
  );
};

export const SlackSetupScreen: React.FC<SlackSetupScreenProps> = ({
  workspace,
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const { mutate: disconnectSlack, isPending: isDisconnecting } =
    useDisconnectSlack();
  const queryClient = useQueryClient();

  const handleSuccess = async () => {
    try {
      await queryClient.refetchQueries({ queryKey: ['workspaces'] });
      await onComplete();
    } catch (err) {
      console.error('Failed to complete Slack setup:', err);
      setError(t('failedToSaveSlackConfigurationPleaseTry'));
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleDisconnect = () => {
    disconnectSlack(undefined, {
      onSuccess: async () => {
        setIsConfirming(false);
        try {
          await onComplete();
        } catch (err) {
          console.error('Failed to complete after disconnect:', err);
          setError(t('slackDisconnectedButFailedToRefreshPlease'));
        }
      },
      onError: (err: unknown) => {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to disconnect Slack. Please try again.',
        );
        setIsConfirming(false);
      },
    });
  };

  const { data: workspaces, isFetched } = useWorkspaces();
  const liveSlack = workspaces?.find((w) => w.workspaceType === WorkspaceType.SLACK);
  const isReconfiguring = isFetched ? (liveSlack?.enabled ?? false) : workspace.enabled;

  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">
          {isReconfiguring ? t('reconfigureSlack') : t('common:slack')}
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-[#FF732E]/10 blur-xl" />
            {workspace.image ? (
              <Image
                name={workspace.image.name}
                type={workspace.image.type}
                className="relative h-20 w-20 rounded-xl object-cover"
                alt={workspace.name}
                fileExtension={workspace.image.fileExtension}
              />
            ) : (
              <div className="relative flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF732E]/20 to-[#E65A1F]/20">
                <span className="text-4xl leading-tight">{workspace.icon}</span>
              </div>
            )}
          </div>

          {/* Short description */}
          <div className="mt-4">
            {isReconfiguring ? (
              <>
                <div className="mb-2 flex items-center justify-center gap-2 text-sm font-medium text-green-600">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {workspace.config?.teamName
                    ? t('slackIsAlreadyConnectedToWorkspace', {
                        teamName: workspace.config.teamName,
                      })
                    : t('slackIsAlreadyConnectedToAWorkspace')}
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('wantToConnectToADifferentWorkspace')}
                </p>
              </>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                {t('iLlBeRightThereInOur')}
              </p>
            )}
          </div>
        </div>

        {/* 4. Learn More Link */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">
            <a
              href="https://docs.pleri.ai/tools/slack"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('clickHereToLearnMore')}
            </a>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* 6. Call to Action Button */}
        <div className="mb-6 flex flex-col items-center">
          <SlackInstallButton
            onSuccess={handleSuccess}
            onError={handleError}
            disabled={isLoading || isConfirming || isDisconnecting}
            isReconfiguring={isReconfiguring}
          />
        </div>

        {/* 7. Cancel / Disconnect */}
        <div className="flex flex-col items-center gap-3">
          {isReconfiguring && isConfirming ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/40">
              <p className="text-center text-sm text-red-700 dark:text-red-400">
                {t('thisWillRemovePleriFromYourSlack')}
              </p>
              <div className="flex items-center gap-6">
                <button
                  onClick={() => setIsConfirming(false)}
                  disabled={isDisconnecting}
                  className="py-2 text-sm text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                  className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isDisconnecting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      {t('disconnecting')}
                    </>
                  ) : (
                    t('disconnect')
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>
              {isReconfiguring && (
                <button
                  onClick={() => setIsConfirming(true)}
                  disabled={isLoading}
                  className="text-sm text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  {t('disconnectSlack')}
                </button>
              )}
              <button
                onClick={onCancel}
                disabled={isLoading}
                className="mt-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                {t('common:cancel')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
